import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Trash2, Edit2, Send, Calendar, Link as LinkIcon,
  Megaphone, X, Loader2, AlertTriangle, Archive, Video,
  Bell, Users, ExternalLink, Plug, RefreshCw, UserX, UserCheck, CheckCircle2, Mail, Eye,
  Upload, FileText, Copy, Search,
} from "lucide-react";
import { LOGO_URL } from "../lib/brand";
import AdminTopNav from "../components/AdminTopNav";
import WebinarTargetedInviteModal from "../components/admin/WebinarTargetedInviteModal";
import ReminderAudienceListModal from "../components/admin/ReminderAudienceListModal";
import { BACKEND_URL } from "../lib/apiBase";
import { API, fmtDateTime, isPast } from "../components/admin/webinars/utils";
import AudiencePresets from "../components/admin/webinars/AudiencePresets";
import ManualImportModal from "../components/admin/webinars/ManualImportModal";
import WebinarEditor from "../components/admin/webinars/WebinarEditor";
import WebinarBroadcastModal from "../components/admin/webinars/WebinarBroadcastModal";
import ReminderListModal from "../components/admin/webinars/ReminderListModal";
import ZohoConnectCard from "../components/admin/webinars/ZohoConnectCard";

/* ---------- Reminder audience stat ---------- */
/* Compact inline stat showing how many recipients will receive each
 * automatic pre-webinar reminder (7-day + 24-hour). Read-only — shows
 * the live count plus a tooltip with the full filter breakdown. If
 * either reminder has already been dispatched, we say so and grey it
 * out so the admin knows the bus has left the station.
 */
function ReminderAudienceStat({ data, scheduledAt, slug, onOpenList }) {
  const seven = data?.["7d"] || {};
  const tf = data?.["24h"] || {};
  // Calc when each reminder will fire (approx) so the admin can plan
  const fireAt7d = scheduledAt ? new Date(new Date(scheduledAt).getTime() - 7 * 24 * 3600 * 1000) : null;
  const fireAt24h = scheduledAt ? new Date(new Date(scheduledAt).getTime() - 24 * 3600 * 1000) : null;
  const fmt = (d) => d ? d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";

  const Pill = ({ label, count, fireAt, dispatched, breakdown, cohort }) => {
    const tooltip = [
      `Auslöser: ${dispatched ? "BEREITS VERSENDET" : fmt(fireAt)}`,
      `→ ${count} Empfänger erhalten die Mail`,
      ...Object.entries(breakdown)
        .filter(([k, v]) => v > 0 && k !== "eligible" && k !== "already_dispatched")
        .map(([k, v]) => {
          const labels = {
            attended: "haben bereits an einem Webinar teilgenommen",
            opt_out: "Opt-out oder abgelehnt",
            registered: "schon für DIESES Webinar registriert",
            already_clicked: "haben bereits den Invite-Link geklickt",
            already_reminded: "haben den Reminder schon erhalten",
          };
          return `· ${v} ${labels[k] || k} (übersprungen)`;
        }),
      "",
      "Klick → Liste der Empfänger anzeigen",
    ].join("\n");
    return (
      <button
        type="button"
        title={tooltip}
        onClick={() => onOpenList?.(cohort)}
        data-testid={`reminder-stat-${label.toLowerCase()}-${slug}`}
        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold border transition-all ${
          dispatched
            ? "bg-slate-100 text-slate-400 border-slate-200 line-through hover:bg-slate-200"
            : "bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300 hover:shadow-sm"
        }`}
      >
        <Mail className="h-3 w-3" />
        <span>{label}</span>
        <span className="font-display font-bold">{count}</span>
        {dispatched && <span className="ml-0.5 text-[9px] uppercase tracking-wider">versendet</span>}
      </button>
    );
  };

  return (
    <div className="mt-2.5 flex flex-wrap items-center gap-2">
      <span className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Auto-Reminder</span>
      <Pill
        label="7d"
        cohort="7d"
        count={seven.eligible || 0}
        fireAt={fireAt7d}
        dispatched={seven.already_dispatched}
        breakdown={seven}
      />
      <Pill
        label="24h"
        cohort="24h"
        count={tf.eligible || 0}
        fireAt={fireAt24h}
        dispatched={tf.already_dispatched}
        breakdown={tf}
      />
      {(seven.attended > 0 || tf.attended > 0) && (
        <span
          className="text-[10px] text-slate-500"
          title={`${Math.max(seven.attended, tf.attended)} Empfänger haben in der Vergangenheit an einem Webinar teilgenommen — werden vom Auto-Reminder übersprungen.`}
        >
          ({Math.max(seven.attended, tf.attended)} Teilnehmer übersprungen)
        </span>
      )}
    </div>
  );
}

export default function AdminWebinars() {
  const navigate = useNavigate();
  const token = typeof window !== "undefined" ? localStorage.getItem("alphalane_admin_token") : null;
  const [webinars, setWebinars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = closed, {} = new, {id,...} = editing
  const [broadcastFor, setBroadcastFor] = useState(null); // webinar object
  const [remindersFor, setRemindersFor] = useState(null); // webinar object

  const headers = { "X-Admin-Token": token };

  useEffect(() => {
    if (!token) { navigate("/admin/login"); return; }
    fetchList();
    // OAuth-Callback liefert ?zoho=connected — Toast + URL säubern.
    const params = new URLSearchParams(window.location.search);
    const z = params.get("zoho");
    if (z === "connected") {
      toast.success("Zoho Webinar verbunden! 🎉");
      window.history.replaceState({}, "", window.location.pathname);
    } else if (z) {
      toast.error(`Zoho-Verbindung fehlgeschlagen (${z}). Bitte erneut versuchen.`);
      window.history.replaceState({}, "", window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchList = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/webinars`, { headers });
      setWebinars(data || []);
    } catch { toast.error("Konnte Webinare nicht laden."); }
    finally { setLoading(false); }
  };

  const removeWebinar = async (w) => {
    if (!window.confirm(`Webinar "${w.title}" wirklich löschen? Schon versandte Einladungen bleiben unberührt.`)) return;
    try {
      await axios.delete(`${API}/admin/webinars/${w.id}`, { headers });
      toast.success("Webinar gelöscht.");
      fetchList();
    } catch { toast.error("Löschen fehlgeschlagen."); }
  };

  const archiveWebinar = async (w) => {
    try {
      await axios.post(`${API}/admin/webinars/${w.id}/archive`, {}, { headers });
      toast.success("Webinar archiviert.");
      fetchList();
    } catch { toast.error("Archivieren fehlgeschlagen."); }
  };

  return (
    <div className="min-h-screen bg-[var(--al-bg)]">
      <header className="border-b border-[var(--al-line)] bg-white/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Alphalane" className="h-9 w-9 object-contain" />
            <div>
              <div className="font-display font-bold tracking-tight">Admin · Webinare</div>
              <div className="text-xs text-slate-500">Anstehende Webinare verwalten · Einladungen versenden</div>
            </div>
          </div>
          <button
            onClick={() => setEditing({})}
            data-testid="webinar-new-btn"
            className="al-cta"
          >
            <Plus className="h-4 w-4" /> Neues Webinar
          </button>
        </div>
        <AdminTopNav />
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-6 sm:py-10 space-y-6">
        <ZohoConnectCard token={token} />
        <section className="al-card p-5 sm:p-7">
          <div className="flex items-center gap-2 mb-1 text-[var(--al-purple)]">
            <Video className="h-5 w-5" />
            <h2 className="font-display font-bold text-lg">Anstehende & vergangene Webinare</h2>
          </div>
          <p className="text-sm text-slate-500 mb-5">
            Der Drip-Engine versendet automatisch das <strong>nächste anstehende Webinar</strong> an neue Leads (4 Std. nach Eintragung).
            Für sofortige Massen-Mails an alle Empfänger nutze den "Einladung senden"-Button pro Webinar.
          </p>

          {loading ? (
            <div className="py-10 text-center text-slate-400">
              <Loader2 className="h-6 w-6 mx-auto animate-spin" />
            </div>
          ) : webinars.length === 0 ? (
            <div className="text-center py-10 text-slate-500">
              <Video className="h-10 w-10 mx-auto text-slate-300 mb-3" />
              <p className="font-display font-semibold">Noch keine Webinare angelegt.</p>
              <p className="text-sm mt-1">Klicke oben auf <strong>"Neues Webinar"</strong>, um dein erstes anzulegen.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {webinars.map((w) => (
                <WebinarRow
                  key={w.id}
                  webinar={w}
                  token={token}
                  onEdit={() => setEditing(w)}
                  onDelete={() => removeWebinar(w)}
                  onArchive={() => archiveWebinar(w)}
                  onBroadcast={() => setBroadcastFor(w)}
                  onReminders={() => setRemindersFor(w)}
                />
              ))}
            </div>
          )}
        </section>
      </main>

      {editing !== null && (
        <WebinarEditor
          webinar={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); fetchList(); }}
          token={token}
        />
      )}

      {broadcastFor && (
        <WebinarBroadcastModal
          webinar={broadcastFor}
          onClose={() => setBroadcastFor(null)}
          token={token}
        />
      )}

      {remindersFor && (
        <ReminderListModal
          webinar={remindersFor}
          onClose={() => setRemindersFor(null)}
          token={token}
        />
      )}
    </div>
  );
}

/* ---------- Row ---------- */
function WebinarRow({ webinar, token, onEdit, onDelete, onArchive, onBroadcast, onReminders }) {
  const past = isPast(webinar.scheduled_at);
  const [syncing, setSyncing] = useState(false);
  const [syncingRegistrants, setSyncingRegistrants] = useState(false);
  const [sendingNoshow, setSendingNoshow] = useState(false);
  const [manualImportOpen, setManualImportOpen] = useState(false);
  // Selective-invite (typeahead → multi-pick) modal state. Self-contained
  // — no preview/queue logic here, the modal handles its own search + send
  // and reports back via toast.
  const [targetedInviteOpen, setTargetedInviteOpen] = useState(false);
  // Reminder-audience-list modal: null = closed, "7d"/"24h" = open on that cohort.
  const [reminderListCohort, setReminderListCohort] = useState(null);

  const syncAttendance = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const r = await axios.post(
        `${API}/admin/zoho-webinar/sync/${webinar.id}`,
        {},
        { headers: { "X-Admin-Token": token } },
      );
      toast.success(`Teilnahme synchronisiert: ${r.data.attendees_count} Teilnehmer aus Zoho, ${r.data.reminders_marked} Anmeldungen markiert.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync fehlgeschlagen.");
    } finally {
      setSyncing(false);
    }
  };

  const syncRegistrants = async () => {
    if (syncingRegistrants) return;
    setSyncingRegistrants(true);
    try {
      const r = await axios.post(
        `${API}/admin/zoho-webinar/sync-registrants/${webinar.id}`,
        {},
        { headers: { "X-Admin-Token": token } },
      );
      toast.success(`Registranten synchronisiert: ${r.data.registrants_count} bei Zoho · ${r.data.rows_stamped} im CRM markiert.`);
      // Reload list so the new count shows.
      await loadRegistrants(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Registranten-Sync fehlgeschlagen.");
    } finally {
      setSyncingRegistrants(false);
    }
  };

  // Registrants list — fetched on demand (when the user clicks "Liste zeigen")
  // and after every sync.
  const [registrants, setRegistrants] = useState(null);
  const [registrantsOpen, setRegistrantsOpen] = useState(false);
  const [registrantsLoading, setRegistrantsLoading] = useState(false);

  const loadRegistrants = async (force = false) => {
    if (registrantsLoading) return;
    if (registrants && !force) {
      setRegistrantsOpen((v) => !v);
      return;
    }
    setRegistrantsLoading(true);
    try {
      const r = await axios.get(
        `${API}/admin/zoho-webinar/registrants/${webinar.id}`,
        { headers: { "X-Admin-Token": token } },
      );
      setRegistrants(r.data);
      setRegistrantsOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Liste konnte nicht geladen werden.");
    } finally {
      setRegistrantsLoading(false);
    }
  };

  const [postMailingPreview, setPostMailingPreview] = useState(null);
  const [postMailingLoading, setPostMailingLoading] = useState(false);

  const previewPostMailing = async () => {
    setPostMailingLoading(true);
    try {
      const r = await axios.post(
        `${API}/admin/webinars/${webinar.id}/post-mailing?dry_run=true`,
        {},
        { headers: { "X-Admin-Token": token } },
      );
      setPostMailingPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Vorschau fehlgeschlagen.");
    } finally {
      setPostMailingLoading(false);
    }
  };

  const sendPostMailing = async () => {
    setPostMailingLoading(true);
    try {
      const r = await axios.post(
        `${API}/admin/webinars/${webinar.id}/post-mailing`,
        {},
        { headers: { "X-Admin-Token": token } },
      );
      const s = r.data.sent || {};
      toast.success(
        `Post-Webinar-Mails versendet: ${s.attended || 0} Teilnehmer · ${s.noshow || 0} No-Shows · ${s.missed || 0} Andere.`
      );
      setPostMailingPreview(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Versand fehlgeschlagen.");
    } finally {
      setPostMailingLoading(false);
    }
  };

  const sendNoshow = async () => {
    if (sendingNoshow) return;
    if (!window.confirm("No-Show-Followup-Mail an alle Registrierten ohne Teilnahme senden? (Dedup-geschützt)")) return;
    setSendingNoshow(true);
    try {
      const r = await axios.post(
        `${API}/admin/zoho-webinar/send-noshow-followup/${webinar.id}`,
        {},
        { headers: { "X-Admin-Token": token } },
      );
      toast.success(`No-Show-Followup: ${r.data.sent} versendet, ${r.data.skipped_already} bereits erhalten, ${r.data.errors} Fehler.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send fehlgeschlagen.");
    } finally {
      setSendingNoshow(false);
    }
  };

  // ─── Manual attendance import ───────────────────────────────────────
  // Used when Zoho's API attendance sync isn't reachable (account/scope
  // limitations). Pascal pastes the copied Zoho-Teilnehmerliste; the
  // backend parses Email + Ja/Nein, upserts webinar_reminders, and
  // creates the same attendance snapshot the real sync would. After
  // that, the regular Post-Webinar-Mailing button works as usual.
  const [attendanceImportOpen, setAttendanceImportOpen] = useState(false);
  const [attendanceImportText, setAttendanceImportText] = useState("");
  const [attendanceImportLoading, setAttendanceImportLoading] = useState(false);
  const [attendanceImportPreview, setAttendanceImportPreview] = useState(null);

  const submitAttendanceImport = async (dryRun) => {
    if (attendanceImportLoading) return;
    if (!attendanceImportText.trim()) {
      toast.error("Bitte zuerst die Teilnehmerliste einfügen.");
      return;
    }
    setAttendanceImportLoading(true);
    try {
      const form = new FormData();
      form.append("text", attendanceImportText);
      const r = await axios.post(
        `${API}/admin/zoho-webinar/manual-attendance/${webinar.id}${dryRun ? "?dry_run=true" : ""}`,
        form,
        { headers: { "X-Admin-Token": token } },
      );
      if (dryRun) {
        setAttendanceImportPreview(r.data);
      } else {
        toast.success(
          `Teilnahme importiert: ${r.data.attended} Teilnehmer · ${r.data.noshow} No-Shows · ${r.data.reminders_created} neu angelegt, ${r.data.reminders_updated} aktualisiert.`
        );
        setAttendanceImportOpen(false);
        setAttendanceImportText("");
        setAttendanceImportPreview(null);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import fehlgeschlagen.");
    } finally {
      setAttendanceImportLoading(false);
    }
  };

  // ─── Reminder audience preview (7d + 24h counts) ────────────────────
  // Read-only stats so the admin sees how many recipients each pre-webinar
  // reminder will reach, plus the breakdown of attendance/opt-out filters.
  // Lazy-loaded on first render to avoid 1 DB sweep per webinar row when
  // the admin only wants to see a single one.
  const [reminderPreview, setReminderPreview] = useState(null);
  const [reminderPreviewLoading, setReminderPreviewLoading] = useState(false);

  useEffect(() => {
    if (past || webinar.is_archived || webinar.kind === "technical") return;
    let cancelled = false;
    setReminderPreviewLoading(true);
    axios
      .get(`${API}/admin/webinars/${webinar.id}/reminder-preview`, {
        headers: { "X-Admin-Token": token },
      })
      .then((r) => { if (!cancelled) setReminderPreview(r.data); })
      .catch(() => { /* silent — stat is non-critical */ })
      .finally(() => { if (!cancelled) setReminderPreviewLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webinar.id]);

  // ─── Tech-Webinar invite (audience-selectable) ─────────────────────
  // Only relevant for kind="technical" webinars in the future. Sends
  // WEBINAR_TECH_INVITE to the admin-selected union of cohorts.
  // 48h after open without registration, the scheduler dispatches the
  // WEBINAR_TECH_REMINDER_48H automatically — Adrian doesn't have to
  // click anything for that follow-up.
  const [techInviteLoading, setTechInviteLoading] = useState(false);
  const [techInvitePreview, setTechInvitePreview] = useState(null);
  // Cohort selection persists across modal open/close so admin doesn't
  // have to re-tick the same boxes when re-running the preview.
  const [techInviteAudiences, setTechInviteAudiences] = useState(
    () => new Set(["past_attendees"]),
  );

  const toggleAudience = (key) => {
    setTechInviteAudiences((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      // Always require at least one cohort.
      if (next.size === 0) next.add("past_attendees");
      return next;
    });
  };

  const fetchTechInvitePreview = async (audSet = techInviteAudiences) => {
    if (techInviteLoading) return;
    setTechInviteLoading(true);
    try {
      const audParam = Array.from(audSet).join(",") || "past_attendees";
      const r = await axios.post(
        `${API}/admin/webinars/${webinar.id}/invite-past-attendees?dry_run=true&audiences=${encodeURIComponent(audParam)}`,
        {},
        { headers: { "X-Admin-Token": token } },
      );
      setTechInvitePreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Vorschau fehlgeschlagen.");
    } finally {
      setTechInviteLoading(false);
    }
  };

  const sendTechInvite = async () => {
    if (techInviteLoading) return;
    const n = techInvitePreview?.candidates ?? "?";
    if (!window.confirm(`Einladung an ${n} Empfänger senden? (max. 1 Mail je Person, 48h-Reminder läuft automatisch.)`)) return;
    setTechInviteLoading(true);
    try {
      const audParam = Array.from(techInviteAudiences).join(",") || "past_attendees";
      const r = await axios.post(
        `${API}/admin/webinars/${webinar.id}/invite-past-attendees?audiences=${encodeURIComponent(audParam)}`,
        {},
        { headers: { "X-Admin-Token": token } },
      );
      const sentN = r.data.sent ?? 0;
      const failN = r.data.failed ?? 0;
      const failsList = r.data.failures || [];
      if (failN === 0) {
        toast.success(`Tech-Einladung versendet: ${sentN} ok.`);
      } else {
        // Log full reasons to console so the user can copy-paste them
        // when reporting an issue. Toast stays compact.
        console.warn("[tech-invite] failures:", failsList);
        const firstReason = failsList[0]?.reason || "send_failed";
        toast.warning(
          `Tech-Einladung: ${sentN} ok, ${failN} Fehler (Grund: ${firstReason}). Konsole für Details.`,
          { duration: 10000 },
        );
      }
      setTechInvitePreview(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Versand fehlgeschlagen.");
    } finally {
      setTechInviteLoading(false);
    }
  };

  // ─── Tech-invite CORRECTION mail (one-shot apology after misfire) ───────
  // Sends WEBINAR_TECH_INVITE_CORRECTION to everyone who got the WEBINAR_TECH_INVITE
  // for this slug — signed by Pascal, opens up the call as a goodwill gesture.
  const [correctionLoading, setCorrectionLoading] = useState(false);
  const [correctionPreview, setCorrectionPreview] = useState(null);

  const fetchCorrectionPreview = async () => {
    if (correctionLoading) return;
    setCorrectionLoading(true);
    try {
      const r = await axios.post(
        `${API}/admin/webinars/${webinar.id}/send-tech-correction?dry_run=true`,
        {},
        { headers: { "X-Admin-Token": token } },
      );
      setCorrectionPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Vorschau fehlgeschlagen.");
    } finally {
      setCorrectionLoading(false);
    }
  };

  const sendCorrection = async () => {
    if (correctionLoading) return;
    const n = correctionPreview?.candidates ?? "?";
    if (!window.confirm(
      `Korrektur-Mail an ${n} Empfänger senden? Diese haben die Tech-Einladung bereits bekommen.`
    )) return;
    setCorrectionLoading(true);
    try {
      const r = await axios.post(
        `${API}/admin/webinars/${webinar.id}/send-tech-correction`,
        {},
        { headers: { "X-Admin-Token": token } },
      );
      toast.success(`Korrektur versendet: ${r.data.sent} ok, ${r.data.failed} Fehler.`);
      setCorrectionPreview(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Versand fehlgeschlagen.");
    } finally {
      setCorrectionLoading(false);
    }
  };

  return (
    <div
      data-testid={`webinar-row-${webinar.slug}`}
      className={`rounded-2xl border bg-white p-4 sm:p-5 flex flex-col gap-3 ${past ? "opacity-60 border-slate-200" : "border-[var(--al-line)]"}`}
    >
      <div className="flex flex-col lg:flex-row gap-4 lg:items-center">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-display font-semibold text-slate-900 text-base sm:text-lg">{webinar.title}</h3>
          {webinar.kind === "technical" && (
            <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-bold bg-sky-100 text-sky-800" title="Technik-Webinar — wird nicht automatisch in Post-Mails verlinkt">technik</span>
          )}
          {past && <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-bold bg-slate-200 text-slate-600">vorbei</span>}
          {webinar.is_archived && <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-bold bg-amber-100 text-amber-800">archiviert</span>}
        </div>
        <div className="text-sm text-slate-500 mt-1 flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="inline-flex items-center gap-1.5">
            <Calendar className="h-3.5 w-3.5" />
            <strong className="text-slate-700">{fmtDateTime(webinar.scheduled_at)}</strong>
          </span>
          <a
            href={webinar.zoho_link}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-[var(--al-purple)] hover:underline max-w-full overflow-hidden"
            title={webinar.zoho_link}
          >
            <LinkIcon className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate font-mono text-xs">{webinar.zoho_link}</span>
          </a>
        </div>
        {webinar.description && (
          <p className="text-xs text-slate-500 mt-2 max-w-3xl">{webinar.description}</p>
        )}
        {reminderPreview && !past && !webinar.is_archived && webinar.kind !== "technical" && (
          <ReminderAudienceStat
            data={reminderPreview}
            scheduledAt={webinar.scheduled_at}
            slug={webinar.slug}
            onOpenList={(cohort) => setReminderListCohort(cohort)}
          />
        )}
        {reminderPreviewLoading && !reminderPreview && !past && webinar.kind !== "technical" && (
          <div className="mt-2 text-[11px] text-slate-400 inline-flex items-center gap-1.5">
            <Loader2 className="h-3 w-3 animate-spin" /> Reminder-Statistik wird geladen…
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 shrink-0">
        {!past && !webinar.is_archived && (
          <>
            {webinar.kind !== "technical" && (
              <button
                onClick={onBroadcast}
                data-testid={`webinar-broadcast-${webinar.slug}`}
                className="al-cta-ghost text-xs"
                title="Einladung an alle Leads/Kunden senden"
              >
                <Megaphone className="h-4 w-4" /> Einladung senden
              </button>
            )}
            <button
              onClick={() => setTargetedInviteOpen(true)}
              data-testid={`webinar-targeted-invite-${webinar.slug}`}
              className="al-cta-ghost text-xs"
              title={webinar.kind === "technical"
                ? "Technik-Einladung an einzelne, namentlich ausgewählte Kontakte schicken"
                : "Einladung an einzelne, namentlich ausgewählte Leads schicken"}
            >
              <Send className="h-4 w-4" /> Selektiv einladen
            </button>
            <button
              onClick={() => setManualImportOpen(true)}
              data-testid={`webinar-registrants-import-${webinar.slug}`}
              className="al-cta-ghost text-xs"
              title="Manuell importieren: Zoho CSV-Export hochladen oder Liste einfügen — Fallback wenn die Zoho-API nicht antwortet"
            >
              <Upload className="h-4 w-4" />
              <span>CSV/Text importieren</span>
            </button>
            <button
              onClick={() => loadRegistrants()}
              disabled={registrantsLoading}
              data-testid={`webinar-registrants-list-${webinar.slug}`}
              className="al-cta-ghost text-xs disabled:opacity-50"
              title="Zoho-Registranten-Liste anzeigen"
            >
              {registrantsLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
              <span>{webinar.registrants_count != null ? `Liste (${webinar.registrants_count})` : "Liste zeigen"}</span>
            </button>
          </>
        )}
        {past && (
          <>
            <button
              onClick={syncAttendance}
              disabled={syncing}
              data-testid={`webinar-zoho-sync-${webinar.slug}`}
              className="al-cta-ghost text-xs disabled:opacity-50"
              title="Teilnehmerliste aus Zoho holen und Anmeldungen markieren"
            >
              {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              <span>Teilnahme syncen</span>
            </button>
            <button
              onClick={() => setAttendanceImportOpen(true)}
              data-testid={`webinar-attendance-manual-${webinar.slug}`}
              className="al-cta-ghost text-xs"
              title="Teilnehmerliste manuell einfügen (z.B. aus Zoho-Bericht kopiert) — Alternative wenn der API-Sync nicht funktioniert"
            >
              <Upload className="h-4 w-4" />
              <span>Teilnahme manuell</span>
            </button>
            <button
              onClick={previewPostMailing}
              disabled={postMailingLoading}
              data-testid={`webinar-post-mailing-${webinar.slug}`}
              className="al-cta text-xs disabled:opacity-50"
              title="Drei Mails versenden: Danke an Teilnehmer · Hinweis auf nächstes Webinar an No-Shows · 'Verpasst?'-Mail an alle anderen Leads"
            >
              {postMailingLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
              <span>Post-Webinar-Mailing</span>
            </button>
            <button
              onClick={sendNoshow}
              disabled={sendingNoshow}
              data-testid={`webinar-noshow-${webinar.slug}`}
              className="al-cta-ghost text-xs disabled:opacity-50"
              title="Nur No-Show-Mail (alte Variante, sendet ohne Teilnehmer-Danke und Rest-Reaktivierung)"
            >
              {sendingNoshow ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserX className="h-4 w-4" />}
              <span>Nur No-Show</span>
            </button>
          </>
        )}
        {!past && webinar.kind === "technical" && (
          <>
            <button
              onClick={() => fetchTechInvitePreview()}
              disabled={techInviteLoading}
              data-testid={`webinar-tech-invite-${webinar.slug}`}
              className="al-cta text-xs disabled:opacity-50"
              title="Technik-Einladung an wählbare Empfängergruppen senden (Teilnehmer/Leads/Kunden/Kontakte/Partner). 48h-Reminder läuft automatisch."
            >
              {techInviteLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserCheck className="h-4 w-4" />}
              <span>Technik-Einladung versenden</span>
            </button>
          </>
        )}
        <button
          onClick={onReminders}
          data-testid={`webinar-reminders-${webinar.slug}`}
          className="p-2 rounded-lg hover:bg-emerald-50 text-slate-500 hover:text-emerald-700"
          title="WhatsApp-Reminder Opt-ins"
        >
          <Bell className="h-4 w-4" />
        </button>
        <button onClick={onEdit} data-testid={`webinar-edit-${webinar.slug}`} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-800" title="Bearbeiten">
          <Edit2 className="h-4 w-4" />
        </button>
        {!webinar.is_archived && (
          <button onClick={onArchive} data-testid={`webinar-archive-${webinar.slug}`} className="p-2 rounded-lg hover:bg-amber-50 text-slate-500 hover:text-amber-700" title="Archivieren (wird nicht mehr im Drip versendet)">
            <Archive className="h-4 w-4" />
          </button>
        )}
        <button onClick={onDelete} data-testid={`webinar-delete-${webinar.slug}`} className="p-2 rounded-lg hover:bg-rose-50 text-slate-500 hover:text-rose-600" title="Löschen">
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
      </div>

      {/* Registrants drawer — collapsible, only renders after the user clicked "Liste zeigen" or "Registranten syncen". */}
      {registrantsOpen && registrants && (
        <div className="rounded-xl border border-violet-200 bg-violet-50/40 p-3 sm:p-4" data-testid={`registrants-panel-${webinar.slug}`}>
          <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
            <div className="font-display font-semibold text-sm text-slate-800 flex items-center gap-2">
              <Users className="h-4 w-4 text-violet-600" />
              Zoho-Registranten ({registrants.count})
              {registrants.synced_at && (
                <span className="text-[11px] font-normal text-slate-500">
                  · zuletzt synchronisiert {fmtDateTime(registrants.synced_at)}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={() => setRegistrantsOpen(false)}
              className="text-slate-400 hover:text-slate-700 text-xs"
            >
              Schliessen
            </button>
          </div>
          {registrants.count === 0 ? (
            <div className="text-xs text-slate-500 bg-white rounded-lg p-3">
              Noch keine Registranten — beim ersten Sync werden alle bisherigen
              Zoho-Anmeldungen abgeholt.
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-violet-100 max-h-[420px] overflow-y-auto" data-testid={`registrants-list-${webinar.slug}`}>
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-50 border-b border-slate-200">
                  <tr className="text-slate-500 uppercase tracking-wider text-[10px]">
                    <th className="text-left p-2.5 font-semibold">Name</th>
                    <th className="text-left p-2.5 font-semibold">E-Mail</th>
                    <th className="text-left p-2.5 font-semibold">Quelle</th>
                    <th className="text-left p-2.5 font-semibold">Partner</th>
                    <th className="text-left p-2.5 font-semibold">Angemeldet</th>
                  </tr>
                </thead>
                <tbody>
                  {registrants.registrants.map((r, idx) => {
                    const crm = r._crm || {};
                    const fullName = (
                      [r.first_name, r.last_name].filter(Boolean).join(" ").trim()
                      || crm.name
                      || "—"
                    );
                    const kindColor =
                      crm.kind === "customer_account" ? "bg-emerald-100 text-emerald-800"
                      : crm.kind === "lead"            ? "bg-violet-100 text-violet-800"
                      : crm.kind === "contact_request" ? "bg-sky-100 text-sky-800"
                      : crm.kind === "partner"         ? "bg-amber-100 text-amber-800"
                      : "bg-slate-100 text-slate-500";
                    const kindLabel =
                      crm.kind === "customer_account" ? "Kunde"
                      : crm.kind === "lead"            ? "Lead"
                      : crm.kind === "contact_request" ? "Anfrage"
                      : crm.kind === "partner"         ? "Partner"
                      : "Unbekannt";
                    return (
                      <tr key={`${r.email}-${idx}`} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50">
                        <td className="p-2.5 font-display font-semibold text-slate-800">{fullName}</td>
                        <td className="p-2.5 font-mono-tab text-[11px] text-slate-700">{r.email}</td>
                        <td className="p-2.5">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${kindColor}`}>
                            {kindLabel}
                          </span>
                        </td>
                        <td className="p-2.5 text-slate-600 text-[11px]">
                          {crm.partner_slug ? `@${crm.partner_slug}` : <span className="text-slate-400">—</span>}
                        </td>
                        <td className="p-2.5 text-slate-500 text-[11px]">
                          {r.registered_at ? fmtDateTime(r.registered_at) : <span className="text-slate-400">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Post-Webinar-Mailing — Preview/Confirm Modal */}
      {postMailingPreview && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" data-testid="post-mailing-preview">
          <div className="bg-white rounded-2xl w-full max-w-xl p-6 shadow-2xl">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="font-display font-bold text-lg">Post-Webinar-Mailing</h2>
                <p className="text-xs text-slate-500 mt-1">{postMailingPreview.webinar?.title}</p>
              </div>
              <button type="button" onClick={() => setPostMailingPreview(null)} className="text-slate-400 hover:text-slate-700">
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-slate-600 mb-4">
              Jede E-Mail-Adresse bekommt <strong>genau eine</strong> Mail. Reihenfolge: Teilnehmer &gt; No-Shows &gt; Rest.
            </p>
            <div className="space-y-2 mb-5">
              <PostMailRow
                count={postMailingPreview.counts?.attended || 0}
                label="📨 Teilnehmer (Danke + Feedback)"
                samples={postMailingPreview.samples?.attended}
              />
              <PostMailRow
                count={postMailingPreview.counts?.noshow || 0}
                label="🙏 No-Shows (Verständnis + nächster Termin)"
                samples={postMailingPreview.samples?.noshow}
              />
              <PostMailRow
                count={postMailingPreview.counts?.missed || 0}
                label="📬 Alle anderen Leads (Verpasst? Kein Problem)"
                samples={postMailingPreview.samples?.missed}
                disabledHint={!postMailingPreview.next_webinar ? "kein nächstes Webinar gepflegt — wird übersprungen" : null}
              />
            </div>
            {postMailingPreview.next_webinar ? (
              <div className="text-xs text-slate-500 bg-violet-50 border border-violet-100 rounded-lg p-3 mb-4">
                Nächstes Webinar (CTA-Link): <strong className="text-slate-800">{postMailingPreview.next_webinar.title}</strong> · {fmtDateTime(postMailingPreview.next_webinar.scheduled_at)}
              </div>
            ) : (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                ⚠️ Kein nächstes Webinar gepflegt — die Rest-Mail wird übersprungen, da sie keinen CTA-Link hätte.
              </div>
            )}
            {postMailingPreview.skipped?.already_received > 0 && (
              <p className="text-xs text-slate-500 mb-4">
                {postMailingPreview.skipped.already_received} Adressen werden übersprungen, weil sie das Post-Mailing für dieses Webinar bereits erhalten haben.
              </p>
            )}
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setPostMailingPreview(null)}
                className="al-cta-ghost text-sm"
                data-testid="post-mailing-cancel"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={sendPostMailing}
                disabled={postMailingLoading || (
                  (postMailingPreview.counts?.attended || 0) +
                  (postMailingPreview.counts?.noshow || 0) +
                  (postMailingPreview.counts?.missed || 0) === 0
                )}
                className="al-cta text-sm disabled:opacity-50"
                data-testid="post-mailing-confirm"
              >
                {postMailingLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                <span>
                  {(postMailingPreview.counts?.attended || 0) +
                   (postMailingPreview.counts?.noshow || 0) +
                   (postMailingPreview.counts?.missed || 0)}
                  &nbsp;Mails jetzt senden
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
      {manualImportOpen && (
        <ManualImportModal
          webinar={webinar}
          token={token}
          onClose={() => setManualImportOpen(false)}
          onImported={(res) => {
            setManualImportOpen(false);
            toast.success(`${res.registrants_count} Registranten importiert · ${res.rows_stamped} CRM-Rows markiert.`);
            loadRegistrants(true);
          }}
        />
      )}
      {targetedInviteOpen && (
        <WebinarTargetedInviteModal
          webinar={webinar}
          apiBase={API}
          token={token}
          onClose={() => setTargetedInviteOpen(false)}
        />
      )}
      {reminderListCohort && (
        <ReminderAudienceListModal
          webinar={webinar}
          apiBase={API}
          token={token}
          initialCohort={reminderListCohort}
          onClose={() => setReminderListCohort(null)}
        />
      )}
      {attendanceImportOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          data-testid="attendance-import-modal"
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-5 border-b border-slate-200 flex items-start justify-between">
              <div>
                <h3 className="font-display font-bold text-lg">Teilnahme manuell importieren</h3>
                <p className="text-xs text-slate-500 mt-1">
                  {webinar.title} — fügt die Liste ein und markiert Teilnehmer (Ja) / No-Shows (Nein) in der DB.
                </p>
              </div>
              <button
                onClick={() => { setAttendanceImportOpen(false); setAttendanceImportPreview(null); setAttendanceImportText(""); }}
                className="text-slate-400 hover:text-slate-700"
                aria-label="Schließen"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 py-4 overflow-y-auto flex-1">
              <label className="block text-xs font-semibold text-slate-600 mb-2">
                Liste einfügen (Tab- oder Komma-getrennt — eine Person pro Zeile mit Email + Ja/Nein)
              </label>
              <textarea
                value={attendanceImportText}
                onChange={(e) => { setAttendanceImportText(e.target.value); setAttendanceImportPreview(null); }}
                placeholder={`Rini H.\tthinkasmartway@gmail.com\tMay 31, 2026 08:40 PM\tAngenommen\tJa\tSWITZERLAND\nNoah Holzherr\tnoah.holzherr@gmx.ch\tMay 31, 2026 08:15 PM\tAngenommen\tJa\tSWITZERLAND\n…`}
                rows={10}
                className="w-full font-mono text-xs border border-slate-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[var(--al-purple)]"
                data-testid="attendance-import-textarea"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                Es zählt, was in der Spalte „Teilgenommen" steht: <code className="px-1 bg-slate-100 rounded">Ja</code> / <code className="px-1 bg-slate-100 rounded">Nein</code>. Mehrfach-Einträge derselben Email werden zusammengeführt — wer einmal teilgenommen hat, gilt als anwesend.
              </p>

              {attendanceImportPreview && (
                <div className="mt-5 rounded-xl border border-emerald-200 bg-emerald-50/40 px-4 py-3">
                  <div className="text-xs font-semibold text-emerald-800 mb-2">Vorschau (kein Versand)</div>
                  <div className="text-sm text-slate-700">
                    <span className="font-mono">{attendanceImportPreview.parsed_rows}</span> Zeilen erkannt · {" "}
                    <span className="font-mono">{attendanceImportPreview.unique_emails}</span> eindeutige Emails · {" "}
                    <span className="font-mono text-emerald-700">{attendanceImportPreview.attended}</span> Teilnehmer · {" "}
                    <span className="font-mono text-amber-700">{attendanceImportPreview.noshow}</span> No-Shows
                  </div>
                  {attendanceImportPreview.samples?.attended?.length > 0 && (
                    <details className="mt-2 text-xs">
                      <summary className="cursor-pointer text-slate-600">Beispiel-Adressen anzeigen</summary>
                      <div className="mt-1 font-mono text-[11px] text-slate-700 space-y-0.5">
                        <div>✓ {attendanceImportPreview.samples.attended.join(", ")}</div>
                        {attendanceImportPreview.samples.noshow?.length > 0 && (
                          <div>✗ {attendanceImportPreview.samples.noshow.join(", ")}</div>
                        )}
                      </div>
                    </details>
                  )}
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-slate-200 flex flex-wrap gap-3 justify-end">
              <button
                onClick={() => { setAttendanceImportOpen(false); setAttendanceImportPreview(null); setAttendanceImportText(""); }}
                className="al-cta-ghost text-xs"
                disabled={attendanceImportLoading}
                data-testid="attendance-import-cancel"
              >
                Abbrechen
              </button>
              <button
                onClick={() => submitAttendanceImport(true)}
                disabled={attendanceImportLoading || !attendanceImportText.trim()}
                className="al-cta-ghost text-xs disabled:opacity-50"
                data-testid="attendance-import-preview"
              >
                {attendanceImportLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                <span>Vorschau</span>
              </button>
              <button
                onClick={() => submitAttendanceImport(false)}
                disabled={attendanceImportLoading || !attendanceImportText.trim()}
                className="al-cta text-xs disabled:opacity-50"
                data-testid="attendance-import-submit"
              >
                {attendanceImportLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                <span>Importieren &amp; Markieren</span>
              </button>
            </div>
          </div>
        </div>
      )}
      {techInvitePreview && (
        <div
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          data-testid="tech-invite-preview-modal"
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-5 border-b border-slate-200 flex items-start justify-between">
              <div>
                <h3 className="font-display font-bold text-lg">Tech-Einladung versenden</h3>
                <p className="text-xs text-slate-500 mt-1">
                  {webinar.title} — wähle aus, welche Gruppen die Einladung bekommen sollen.
                </p>
              </div>
              <button
                onClick={() => setTechInvitePreview(null)}
                className="text-slate-400 hover:text-slate-700"
                aria-label="Schließen"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 py-5 overflow-y-auto flex-1 space-y-4">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-2">
                  Empfängergruppen
                </div>
                <AudiencePresets
                  accent="sky"
                  activeSet={techInviteAudiences}
                  onApply={(cohorts) => {
                    setTechInviteAudiences(new Set(cohorts));
                    setTechInvitePreview({ ...techInvitePreview, _stale: true });
                  }}
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[
                    { key: "past_attendees", label: "Teilnehmer", hint: "alle, die schon einmal an einem regulären Webinar dabei waren" },
                    { key: "leads",          label: "Leads",       hint: "Interessenten aus dem Lead-Funnel" },
                    { key: "customers",      label: "Kunden",      hint: "zahlende Kunden mit aktivem Account" },
                    { key: "contacts",       label: "Kontakte",    hint: "Kontaktformular-Anfragen (ohne Lead-Status)" },
                    { key: "partners",       label: "Partner",     hint: "approved Partner / Affiliates" },
                  ].map(({ key, label, hint }) => {
                    const active = techInviteAudiences.has(key);
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => {
                          toggleAudience(key);
                          // Reset preview so admin re-runs dry_run with new selection.
                          setTechInvitePreview({ ...techInvitePreview, _stale: true });
                        }}
                        data-testid={`audience-toggle-${key}`}
                        className={`text-left rounded-xl border px-3 py-2.5 transition-all ${
                          active
                            ? "border-sky-500 bg-sky-50 ring-2 ring-sky-200"
                            : "border-slate-200 bg-white hover:border-slate-300"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span
                            className={`inline-flex h-4 w-4 items-center justify-center rounded border-2 ${
                              active ? "border-sky-600 bg-sky-600" : "border-slate-300 bg-white"
                            }`}
                          >
                            {active && <span className="text-white text-[10px] leading-none">✓</span>}
                          </span>
                          <span className="font-display font-semibold text-sm text-slate-900">{label}</span>
                        </div>
                        <div className="text-[11px] text-slate-500 mt-1 ml-6 leading-tight">{hint}</div>
                      </button>
                    );
                  })}
                </div>
                <button
                  type="button"
                  onClick={() => fetchTechInvitePreview()}
                  disabled={techInviteLoading}
                  className="mt-3 text-xs text-sky-700 hover:text-sky-900 underline disabled:opacity-50"
                  data-testid="refresh-audience-preview"
                >
                  {techInviteLoading ? "Aktualisiere…" : "Vorschau mit aktueller Auswahl aktualisieren"}
                </button>
              </div>

              <div className={`rounded-xl border px-4 py-3 ${techInvitePreview._stale ? "border-amber-200 bg-amber-50/50" : "border-sky-200 bg-sky-50/50"}`}>
                <div className={`text-2xl font-display font-bold ${techInvitePreview._stale ? "text-amber-900" : "text-sky-900"}`}>
                  {techInvitePreview._stale ? "—" : `${techInvitePreview.candidates} Empfänger`}
                </div>
                <div className="text-xs text-slate-600 mt-1">
                  {techInvitePreview._stale ? (
                    <em>Auswahl geändert — bitte oben „Vorschau aktualisieren" klicken.</em>
                  ) : (
                    <>({techInvitePreview.unique_attendees} eindeutige Teilnehmer · {techInvitePreview.past_regular_webinars} reguläre Webinare durchsucht)</>
                  )}
                </div>
              </div>
              {!techInvitePreview._stale && techInvitePreview.samples?.length > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-slate-600">Beispiel-Adressen anzeigen</summary>
                  <div className="mt-1 font-mono text-[11px] text-slate-700">
                    {techInvitePreview.samples.join(", ")}
                  </div>
                </details>
              )}
              {!techInvitePreview._stale && (
                <div className="text-xs text-slate-500 space-y-1">
                  <div>Übersprungen (nicht gesendet):</div>
                  <ul className="ml-4 list-disc">
                    <li>{techInvitePreview.skipped?.already_received || 0} bereits eingeladen</li>
                    <li>{techInvitePreview.skipped?.opt_out || 0} Opt-out</li>
                    <li>{techInvitePreview.skipped?.rejected || 0} abgelehnt</li>
                    <li>{techInvitePreview.skipped?.no_crm_match || 0} ohne CRM-Eintrag</li>
                    <li>{techInvitePreview.skipped?.demo || 0} Demo / Test</li>
                  </ul>
                </div>
              )}
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 px-4 py-3 text-xs text-emerald-900">
                <strong>Automatischer 48h-Reminder:</strong> Wer die Einladung öffnet, aber nicht
                anmeldet, bekommt nach 48h automatisch eine freundliche Erinnerung.
                Kein zusätzlicher Klick nötig.
              </div>
            </div>
            <div className="px-6 py-4 border-t border-slate-200 flex flex-wrap gap-3 justify-end">
              <button
                onClick={() => setTechInvitePreview(null)}
                className="al-cta-ghost text-xs"
                disabled={techInviteLoading}
              >
                Abbrechen
              </button>
              <button
                onClick={sendTechInvite}
                disabled={techInviteLoading || !techInvitePreview.candidates || techInvitePreview._stale}
                className="al-cta text-xs disabled:opacity-50"
                data-testid="tech-invite-confirm"
              >
                {techInviteLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                <span>Jetzt versenden</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Korrektur-Mail-Modal (Apology after misfired tech-invite) ─── */}
      {correctionPreview && (
        <div
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          data-testid="tech-correction-preview-modal"
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="px-6 py-5 border-b border-amber-200 bg-amber-50/30 flex items-start justify-between flex-shrink-0">
              <div>
                <h3 className="font-display font-bold text-lg flex items-center gap-2 text-amber-900">
                  <AlertTriangle className="h-5 w-5" />
                  Korrektur-Mail versenden
                </h3>
                <p className="text-xs text-slate-600 mt-1">
                  Entschuldigung + offene Einladung. Geht an alle, die {webinar.title} bereits als Einladung bekommen haben.
                </p>
              </div>
              <button
                onClick={() => setCorrectionPreview(null)}
                className="text-slate-400 hover:text-slate-700"
                aria-label="Schließen"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 py-5 overflow-y-auto flex-1 min-h-0 space-y-3">
              <div className="rounded-xl border border-amber-200 bg-amber-50/50 px-4 py-3">
                <div className="text-2xl font-display font-bold text-amber-900">
                  {correctionPreview.candidates} Empfänger
                </div>
                <div className="text-xs text-slate-600 mt-1">
                  bekommen die Korrektur-Mail (haben WEBINAR_TECH_INVITE für diesen Slug erhalten)
                </div>
              </div>
              {correctionPreview.samples?.length > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-slate-600">Beispiel-Adressen anzeigen</summary>
                  <div className="mt-1 font-mono text-[11px] text-slate-700">
                    {correctionPreview.samples.join(", ")}
                  </div>
                </details>
              )}
              <div className="text-xs text-slate-500 space-y-1">
                <div>Übersprungen (nicht erneut gesendet):</div>
                <ul className="ml-4 list-disc">
                  <li>{correctionPreview.skipped?.already_corrected || 0} Korrektur bereits erhalten</li>
                  <li>{correctionPreview.skipped?.opt_out || 0} Opt-out</li>
                  <li>{correctionPreview.skipped?.rejected || 0} abgelehnt</li>
                  <li>{correctionPreview.skipped?.demo || 0} Demo / Test</li>
                </ul>
              </div>
              <div className="rounded-xl border border-sky-200 bg-sky-50/40 px-4 py-3 text-xs text-sky-900">
                <strong>Was passiert:</strong> Pascal signierte Entschuldigung +
                offene Einladung zum Tech-Call ({fmtDateTime(webinar.scheduled_at)}).
                Adrian's Foto + CTA-Button enthalten. Strikte Dedup pro Empfänger.
              </div>
            </div>
            <div className="px-6 py-4 border-t border-slate-200 flex flex-wrap gap-3 justify-end flex-shrink-0 bg-white">
              <button
                onClick={() => setCorrectionPreview(null)}
                className="al-cta-ghost text-xs"
                disabled={correctionLoading}
              >
                Abbrechen
              </button>
              <button
                onClick={sendCorrection}
                disabled={correctionLoading || !correctionPreview.candidates}
                className="al-cta text-xs disabled:opacity-50"
                data-testid="tech-correction-confirm"
              >
                {correctionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                <span>Jetzt versenden</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PostMailRow({ count, label, samples, disabledHint }) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50/50 px-4 py-3">
      <div className="min-w-0">
        <div className="font-display font-semibold text-sm text-slate-800">{label}</div>
        {samples?.length > 0 && (
          <div className="text-[11px] text-slate-500 mt-1 truncate" title={samples.join(", ")}>
            z.B. {samples.slice(0, 3).join(", ")}{samples.length > 3 ? "…" : ""}
          </div>
        )}
        {disabledHint && <div className="text-[11px] text-amber-700 mt-1">{disabledHint}</div>}
      </div>
      <div className="font-display font-bold text-lg text-[var(--al-purple)] tabular-nums shrink-0">
        {count}
      </div>
    </div>
  );
}
